export interface RewardSummary {
  xp: number;
  diamonds: number;
}

export interface ScenarioRewardSummary extends RewardSummary {
  practiceXp: number;
  practiceDiamonds: number;
  vocabularyXp: number;
  vocabularyDiamonds: number;
  missionXp: number;
  missionDiamonds: number;
}

import {
  LISTEN_REPEAT_XP_PER_CLEAR,
} from "./vocabularyExerciseMetrics";

export interface MissionRewardMetricSource {
  stars?: number;
  xp?: number;
}

interface BuildScenarioRewardSummaryInput {
  passedPracticeItemIdsByScenario: Record<string, string[]>;
  completedVocabularyExerciseKeysByScenario: Record<string, string[]>;
  vocabularyExerciseScoresByScenario: Record<string, Record<string, number>>;
  missionMetricsByScenario: Record<string, MissionRewardMetricSource>;
}

const PRACTICE_XP_PER_CLEAR = 10;
const PRACTICE_DIAMOND_MILESTONE = 5;
const VOCABULARY_DIAMOND_MILESTONE = 5;
const PERFECT_VOCABULARY_SCORE_BY_KIND: Record<string, number> = {
  listen_repeat: 10,
  listen_write: 10,
  unscramble: 10,
};

function clampRewardNumber(value: unknown): number {
  const numeric = Math.round(Number(value) || 0);
  return Number.isFinite(numeric) ? Math.max(0, numeric) : 0;
}

function normalizeStringSet(values: string[] | undefined): string[] {
  if (!Array.isArray(values)) {
    return [];
  }
  return Array.from(
    new Set(
      values
        .map((value) => String(value || "").trim())
        .filter((value) => value.length > 0),
    ),
  );
}

function buildEmptyScenarioRewardSummary(): ScenarioRewardSummary {
  return {
    xp: 0,
    diamonds: 0,
    practiceXp: 0,
    practiceDiamonds: 0,
    vocabularyXp: 0,
    vocabularyDiamonds: 0,
    missionXp: 0,
    missionDiamonds: 0,
  };
}

function sumVocabularyXp(
  scoresByCompletionKey: Record<string, number> | undefined,
  completedExerciseKeys: string[] | undefined,
): number {
  const normalizedScores = scoresByCompletionKey || {};
  const totalRecorded = Object.values(normalizedScores).reduce((sum, value) => sum + clampRewardNumber(value), 0);
  if (!Array.isArray(completedExerciseKeys) || completedExerciseKeys.length <= 0) {
    return totalRecorded;
  }
  const completedKeySet = new Set(completedExerciseKeys);
  let listenRepeatBackfillXp = 0;
  for (const completionKey of completedKeySet) {
    if (!completionKey.startsWith("listen_repeat::")) {
      continue;
    }
    if (clampRewardNumber(normalizedScores[completionKey]) > 0) {
      continue;
    }
    listenRepeatBackfillXp += LISTEN_REPEAT_XP_PER_CLEAR;
  }
  return totalRecorded + listenRepeatBackfillXp;
}

function countPerfectVocabularyClears(
  scoresByCompletionKey: Record<string, number> | undefined,
  completedExerciseKeys: string[] | undefined,
): number {
  const normalizedScores = scoresByCompletionKey || {};
  const completedKeySet = new Set(Array.isArray(completedExerciseKeys) ? completedExerciseKeys : []);
  let perfectCount = 0;
  const completionKeys = new Set<string>([
    ...Object.keys(normalizedScores),
    ...completedKeySet,
  ]);
  for (const completionKey of completionKeys) {
    const exerciseKind = String(completionKey || "").split("::")[0] || "";
    const requiredPerfectScore = PERFECT_VOCABULARY_SCORE_BY_KIND[exerciseKind];
    if (!requiredPerfectScore) {
      continue;
    }
    const rawScore = clampRewardNumber(normalizedScores[completionKey]);
    const resolvedScore = rawScore > 0
      ? rawScore
      : (exerciseKind === "listen_repeat" && completedKeySet.has(completionKey)
        ? LISTEN_REPEAT_XP_PER_CLEAR
        : 0);
    if (resolvedScore >= requiredPerfectScore) {
      perfectCount += 1;
    }
  }
  return perfectCount;
}

function aggregateScenarioRewardSummaries(
  summaries: Map<string, ScenarioRewardSummary>,
  scenarioIds: string[],
): RewardSummary {
  return scenarioIds.reduce<RewardSummary>((totals, scenarioId) => {
    const reward = summaries.get(scenarioId);
    if (!reward) {
      return totals;
    }
    totals.xp += reward.xp;
    totals.diamonds += reward.diamonds;
    return totals;
  }, { xp: 0, diamonds: 0 });
}

export function buildScenarioRewardSummaryById(
  input: BuildScenarioRewardSummaryInput,
): Map<string, ScenarioRewardSummary> {
  const scenarioIds = new Set<string>();
  for (const scenarioId of Object.keys(input.passedPracticeItemIdsByScenario)) {
    if (scenarioId) {
      scenarioIds.add(scenarioId);
    }
  }
  for (const scenarioId of Object.keys(input.completedVocabularyExerciseKeysByScenario)) {
    if (scenarioId) {
      scenarioIds.add(scenarioId);
    }
  }
  for (const scenarioId of Object.keys(input.vocabularyExerciseScoresByScenario)) {
    if (scenarioId) {
      scenarioIds.add(scenarioId);
    }
  }
  for (const scenarioId of Object.keys(input.missionMetricsByScenario)) {
    if (scenarioId) {
      scenarioIds.add(scenarioId);
    }
  }

  const map = new Map<string, ScenarioRewardSummary>();
  for (const scenarioId of scenarioIds) {
    const passedPracticeIds = normalizeStringSet(input.passedPracticeItemIdsByScenario[scenarioId]);
    const practiceClearCount = passedPracticeIds.length;
    const practiceXp = practiceClearCount * PRACTICE_XP_PER_CLEAR;
    const practiceDiamonds = Math.floor(practiceClearCount / PRACTICE_DIAMOND_MILESTONE);

    const vocabularyScores = input.vocabularyExerciseScoresByScenario[scenarioId];
    const completedVocabularyExerciseKeys = input.completedVocabularyExerciseKeysByScenario[scenarioId];
    const vocabularyXp = sumVocabularyXp(vocabularyScores, completedVocabularyExerciseKeys);
    const perfectVocabularyClears = countPerfectVocabularyClears(vocabularyScores, completedVocabularyExerciseKeys);
    const vocabularyDiamonds = Math.floor(perfectVocabularyClears / VOCABULARY_DIAMOND_MILESTONE);

    const missionMetrics = input.missionMetricsByScenario[scenarioId];
    const missionXp = clampRewardNumber(missionMetrics?.xp);
    const missionDiamonds = clampRewardNumber(missionMetrics?.stars);

    map.set(scenarioId, {
      xp: practiceXp + vocabularyXp + missionXp,
      diamonds: practiceDiamonds + vocabularyDiamonds + missionDiamonds,
      practiceXp,
      practiceDiamonds,
      vocabularyXp,
      vocabularyDiamonds,
      missionXp,
      missionDiamonds,
    });
  }

  return map;
}

export function getScenarioRewardSummary(
  summaries: Map<string, ScenarioRewardSummary>,
  scenarioId: string,
): ScenarioRewardSummary {
  return summaries.get(String(scenarioId || "").trim()) || buildEmptyScenarioRewardSummary();
}

export function buildAggregateRewardSummary(
  summaries: Map<string, ScenarioRewardSummary>,
  scenarioIds: string[],
): RewardSummary {
  return aggregateScenarioRewardSummaries(summaries, scenarioIds);
}

export function buildEmptyRewardSummary(): RewardSummary {
  return { xp: 0, diamonds: 0 };
}
